import Box from '@mui/material/Box';
import { SocialFeed } from '../components/social-feed/SocialFeed';
import { VideoBanner, VideoBannerGame } from '../components/video/VideoBanner';
import { HomepageCarousel } from '../components/HomepageCarousel';
import { LiveTournaments } from '../components/competitions/LiveTournaments';
// import { EmailPrompt, EmailPromptProps } from '../components/EmailPrompt';
import { useRef, useState, useMemo, useEffect } from 'react';
import { ItemCardPricedProps } from 'src/components/cards/ItemCardPriced';
import { CollectionCardProps } from 'src/components/cards/CollectionCard';
import { GameCard2Props } from 'src/components/cards/GameCard2';
import { db } from '../config/firebaseAdmin';
import { Item } from 'functions/src/types/firestore/Game/Collection/Item';
import { Game } from 'functions/src/types/firestore/Game';
import { Competition } from 'functions/src/types/firestore/Game/Competition';
import { Collection } from 'functions/src/types/firestore/Game/Collection';
import { getFirestoreData } from 'src/util/getFirestoreData';
import { ConverterFactory } from 'functions/src/util/firestore/ConverterFactory';
import { useMobile } from 'src/hooks/useMobile';
import { Tournament } from 'functions/src/types/firestore/Game/Tournament';
import { ListItemCompetitionProps } from 'src/components/competitions/ListItemCompetition';
import { useAuth } from 'src/contexts/AuthContext';
import { getDocs, query, where, collection } from 'firebase/firestore';
import { GuestlistTeam } from 'functions/src/types/firestore/Game/Tournament/Guestlist';
import { useActionHandler } from 'src/hooks/useActionHandler';

export type HomePageProps = {
  games: (GameCard2Props & VideoBannerGame)[];
  rentalItemCards: ItemCardPricedProps[];
  newListingItemCards: ItemCardPricedProps[];
  collectionCards: CollectionCardProps[];
  leaderboards: Competition<Date>[];
  tournamentsUnpersonalized: Tournament<Date>[];
};
function HomePage({
  games,
  rentalItemCards,
  newListingItemCards,
  collectionCards,
  leaderboards,
  tournamentsUnpersonalized,
}: HomePageProps) {
  const competitionBoardRef = useRef(null);
  const [socialFeedOpen, setSocialFeedOpen] = useState(true);
  // eslint-disable-next-line @blumintinc/blumint/extract-global-constants
  const handleToggle = () => {
    setSocialFeedOpen((prev) => {
      return !prev;
    });
  };

  const isMobile = useMobile();
  const { user } = useAuth();
  useActionHandler();

  // eslint-disable-next-line @blumintinc/blumint/extract-global-constants
  const insertIsRegistered = async (
    userId: string,
    tournaments: Tournament<Date>[],
  ) => {
    // eslint-disable-next-line @blumintinc/blumint/extract-global-constants
    const { firestore } = await import('src/config/firebaseClient');
    return Promise.all(
      tournaments.map(async (tournament) => {
        const teamsRef = collection(
          firestore,
          `Game/${tournament.gameId}/Tournament/${tournament.id}/Guestlist`,
        );
        const teams = await getDocs(
          query(
            teamsRef,
            where('acceptedUserIds', 'array-contains', userId),
          ).withConverter(
            ConverterFactory.buildDateConverter<GuestlistTeam<Date>>(),
          ),
        );
        const team = teams.docs
          .map((doc) => {
            return doc.data();
          })
          .find((teamDetail) => {
            return teamDetail.acceptedUserIds.includes(userId);
          });
        const isRegistered = !!team;
        const isCheckedIn = team?.members.some((member) => {
          return member.userId === userId && member.checkedIn;
        });

        return {
          ...tournament,
          isRegistered,
          isCheckedIn,
        };
      }),
    );
  };
  const [tournamentData, setTournamentData] = useState(
    tournamentsUnpersonalized,
  );
  useEffect(() => {
    // eslint-disable-next-line @blumintinc/blumint/extract-global-constants
    const handler = async () => {
      if (user?.uid) {
        setTournamentData(
          await insertIsRegistered(user?.uid, tournamentsUnpersonalized),
        );
        return;
      }
      setTournamentData(
        tournamentsUnpersonalized.map((tournament) => {
          return {
            ...tournament,
            isRegistered: false,
          };
        }),
      );
    };
    handler();
  }, [tournamentsUnpersonalized, user?.uid]);

  // TODO: DO WE WANT TO SHOW THE OLD UNDEADBLOCKS LEADERBOARDS?
  const competitions = useMemo(() => {
    return [leaderboards[0], ...tournamentData] as (ListItemCompetitionProps &
      Pick<Competition<Date>, 'id' | 'date' | 'endDate'>)[];
  }, [leaderboards, tournamentData]);
  // eslint-disable-next-line @blumintinc/blumint/extract-global-constants
  const shuffleItemCards = (items: ItemCardPricedProps[]) => {
    //TODO: Remove shuffling when we come up with an ordering heuristic.
    let currentIndex = items.length,
      randomIndex: number;
    while (currentIndex !== 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;
      [items[Number(currentIndex)], items[Number(randomIndex)]] = [
        items[Number(randomIndex)],
        items[Number(currentIndex)],
      ];
    }
    return items;
  };
  const [shuffledItemCards, setShuffledItemCards] =
    useState(newListingItemCards);
  useEffect(() => {
    setShuffledItemCards(shuffleItemCards(newListingItemCards));
  }, [newListingItemCards]);
  const paddingTop = isMobile ? 0 : 16;
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          pt: { paddingTop },
          pl: isMobile ? 0 : '24px',
          flexDirection: 'row',
          justifyContent: 'flex-start',
        }}
      >
        {/* <EmailPrompt onEmailChange={onEmailChange} /> */}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              height: '100%',
              width: '100%',
              px: 4,
            }}
          >
            <VideoBanner games={games} />
          </Box>
          <Box
            sx={{
              width: '100%',
              px: 6,
              mb: 4,
            }}
            ref={competitionBoardRef}
          >
            <LiveTournaments competitions={competitions} />
          </Box>
        </Box>
        {!isMobile && (
          <SocialFeed
            competitionBoardRef={competitionBoardRef}
            handleToggle={handleToggle}
            socialFeedOpen={socialFeedOpen}
          />
        )}
      </Box>
      <Box sx={{ ml: isMobile ? 0 : '48px' }}>
        <HomepageCarousel type={'games'} title={'Games'} content={games} />
        <HomepageCarousel
          type={'rentals'}
          title={'Rentals'}
          content={rentalItemCards}
        />
        <HomepageCarousel
          type={'listings'}
          title={'New Listings'}
          content={shuffledItemCards}
        />
        {/* TODO: <HomepageCarousel type={'onSale'} title={'On Sale'} content={items} /> */}
        <HomepageCarousel
          type={'collections'}
          title={'Collections'}
          content={collectionCards}
        />
      </Box>
    </>
  );
}

export async function getStaticProps() {
  const gamesPromise = getFirestoreData(
    db
      .collection('Game')
      .withConverter(ConverterFactory.buildDateConverter<Game<Date>>()),
  );
  //TODO: discuss if we should show whitelisted items here
  // .where('whitelistUserIds', '==', 'null') ??
  const recentRentalOffers = await db
    .collectionGroup('RentalOffer')
    .orderBy('createdAt', 'desc')
    .limit(20)
    .get();
  // TODO: also include marketplace listings
  const rentalItemsPromise = Promise.all(
    recentRentalOffers.docs.map(async (doc) => {
      const itemDoc = await doc.ref.parent.parent
        ?.withConverter(ConverterFactory.buildDateConverter<Item<Date>>())
        .get();
      return itemDoc?.data();
    }),
  );
  //we could at lastListedAt on the data model for this when the marketplace is added
  const newListingItemsPromise = getFirestoreData(
    db
      .collectionGroup('Item')
      .withConverter(ConverterFactory.buildDateConverter<Item<Date>>())
      .where('gameId', '==', 'undead-blocks'),
    //TODO:undo this line when other marketplaces are ready
  );
  const collectionsPromise = getFirestoreData(
    db
      .collectionGroup('Collection')
      .withConverter(ConverterFactory.buildDateConverter<Collection<Date>>()),
  );
  const leaderboardsPromise = getFirestoreData(
    db
      .collectionGroup('Competition')
      .where('type', '==', 'leaderboard')
      .withConverter(ConverterFactory.buildDateConverter<Competition<Date>>()),
  );
  const tournamentsPromise = getFirestoreData(
    db
      .collectionGroup('Tournament')
      .withConverter(ConverterFactory.buildDateConverter<Tournament<Date>>())
      .orderBy('date', 'desc'),
    { limit: 30 },
  );

  const [
    games,
    rentalItems,
    newListingItems,
    collections,
    leaderboards,
    tournaments,
  ] = await Promise.all([
    gamesPromise,
    rentalItemsPromise,
    newListingItemsPromise,
    collectionsPromise,
    leaderboardsPromise,
    tournamentsPromise,
  ]);
  //
  return {
    revalidate: 5,
    props: {
      serverSideProps: 'test',
      rentalItemCards: rentalItems.filter((item) => {
        return !!item;
      }),
      newListingItemCards: newListingItems,
      games: games
        .filter((x) => {
          return x.title !== 'BluMint';
        })
        .sort((a, b) => {
          return a.priority - b.priority;
        }),
      collectionCards: collections,
      leaderboards,
      tournamentsUnpersonalized: tournaments.filter((tournament) => {
        return !!tournament.title;
      }),
      // result,
      // bannerStory.mp4
    },
  };
}

export { HomePage as default };
